/**
 * usePlaybackPreference — reactive hook for the user's Spotify-vs-YouTube
 * primary-player preference.
 *
 * Initializes from localStorage, listens for both `storage` events
 * (changes from other tabs) AND the custom
 * `spotivibly:playback-preference-change` event (changes from this tab,
 * since `storage` events don't fire in the originating tab).
 *
 * Returns [value, set, toggle] tuple — same ergonomics as useState but
 * with cross-component + cross-tab sync built in.
 */

import { useCallback, useEffect, useState } from "react";
import {
  readPlaybackPreference,
  writePlaybackPreference,
  togglePlaybackPreference,
  type PlaybackPreference,
} from "@/lib/playbackPreference";
import { persistPlaybackPreferenceToAccount } from "@/lib/playbackPreferenceSync";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "spotivibly:playback-preference";
const CHANGE_EVENT = "spotivibly:playback-preference-change";

export function usePlaybackPreference(): {
  preference: PlaybackPreference;
  setPreference: (value: PlaybackPreference) => void;
  toggle: () => void;
} {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [preference, setPreferenceState] = useState<PlaybackPreference>(() =>
    readPlaybackPreference(),
  );

  useEffect(() => {
    // Listen for changes from OTHER tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = e.newValue;
      if (next === "spotify" || next === "youtube" || next === "apple_music") {
        setPreferenceState(next);
      }
    };
    // Listen for changes from THIS tab. The `storage` event doesn't fire
    // in the originating tab, so we dispatch a custom event from
    // writePlaybackPreference and pick it up here.
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<PlaybackPreference>).detail;
      if (detail === "spotify" || detail === "youtube" || detail === "apple_music") {
        setPreferenceState(detail);
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGE_EVENT, onCustom as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CHANGE_EVENT, onCustom as EventListener);
    };
  }, []);

  // Write-through to the account so the choice persists across devices until
  // the user changes it again (the account row is the source of truth;
  // AuthContext hydrates localStorage from it on each profile load). Anon
  // users (no userId) stay localStorage-only. Fire-and-forget — the persist
  // helper never throws, and a failed write leaves the device value intact.
  const setPreference = useCallback((value: PlaybackPreference) => {
    // writePlaybackPreference coerces a held platform (Apple Music) to the
    // default; use what it actually wrote so React state + the account row
    // never hold a value the rest of the app refuses to honor.
    const written = writePlaybackPreference(value);
    setPreferenceState(written);
    if (userId) void persistPlaybackPreferenceToAccount(userId, written).catch(() => {});
  }, [userId]);

  const toggle = useCallback(() => {
    const next = togglePlaybackPreference();
    setPreferenceState(next);
    if (userId) void persistPlaybackPreferenceToAccount(userId, next).catch(() => {});
  }, [userId]);

  return { preference, setPreference, toggle };
}
